import React, { useEffect, useState } from "react";
import moment from "moment";
import { useCptFilterLoading } from "../common/useCptFilterLoading";
import { MONTH_OPTIONS } from "../PatientCPTRequirements/common/monthlyPerformanceUtils";
import { MONTHLY_PHONE_CONSULTATION_PROGRAMS } from "./monthlyPhoneConsultationLabels";

const YEAR_OPTIONS = Array.from({ length: 6 }, (_, idx) => String(moment().year() - idx));

const REPORT_FILTER_COPY = {
  patientOutreach: "Applies to Have Met / Have Not Met patient lists.",
  teamPerformance: "Applies to team member consultation counts and comparisons."
};

const PROGRAM_OPTIONS = [
  MONTHLY_PHONE_CONSULTATION_PROGRAMS.rpm,
  MONTHLY_PHONE_CONSULTATION_PROGRAMS.ccm
];

const MonthFilter = ({
  filters,
  onChange,
  onApply,
  onReset,
  loading = false,
  activeReportTab = "patientOutreach",
  programType,
  selectedProgram,
  onProgramChange
}) => {
  const { isFilterBusy, beginSubmit } = useCptFilterLoading(loading);
  const [month, setMonth] = useState(filters.month || moment().format("MM"));
  const [year, setYear] = useState(filters.year || moment().format("YYYY"));
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    setMonth(filters.month || moment().format("MM"));
  }, [filters.month]);

  useEffect(() => {
    setYear(filters.year || moment().format("YYYY"));
  }, [filters.year]);

  const selectedMonthLabel = MONTH_OPTIONS.find((item) => item.value === month)?.label || "";
  const displayMonth = `${selectedMonthLabel} ${year}`.trim();

  const syncFilters = (nextMonth, nextYear) => {
    const nextFilters = { month: nextMonth, year: nextYear };
    setMonth(nextMonth);
    setYear(nextYear);
    onChange(nextFilters);
    return nextFilters;
  };

  const handleApply = () => {
    const selectedDate = moment(`${year}-${month}-01`, "YYYY-MM-DD");
    if (selectedDate.isAfter(moment(), "month")) {
      setValidationMessage("Future month is not allowed.");
      return;
    }

    setValidationMessage("");
    const nextFilters = syncFilters(month, year);
    beginSubmit();
    onApply(nextFilters);
  };

  const handleReset = () => {
    const currentMonth = moment().format("MM");
    const currentYear = moment().format("YYYY");
    setValidationMessage("");
    syncFilters(currentMonth, currentYear);
    if (onReset) {
      onReset();
    }
  };

  return (
    <div className="cpt-filter-wrapper">
      <div className={`mpr-filter-card ${isFilterBusy ? "mpr-filter-card--loading" : ""}`}>
        <div className="mpr-filter-header">
          <div>
            <h4>
              Monthly Phone Consultations
              {selectedProgram ? (
                <span className={`mpc-program-filter-badge ${selectedProgram.label}`}>{selectedProgram.label}</span>
              ) : null}
            </h4>
            <p>
              {selectedProgram?.shortTitle || "Monthly Phone Consultations"} — select month and year, then apply.
              The filter is shared across all sub-reports below.{" "}
              {REPORT_FILTER_COPY[activeReportTab] || REPORT_FILTER_COPY.patientOutreach}
            </p>
          </div>
        </div>

        <div className="mpr-filter-grid mpc-simple-filter-grid">
          <div className="mpr-filter-field mpc-program-switch-field">
            <label>Program</label>
            <div className="mpc-program-switch" role="group" aria-label="Monthly phone consultation program">
              {PROGRAM_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`mpc-program-switch-btn mpc-program-switch-btn--${option.key} ${
                    programType === option.key ? "active" : ""
                  }`}
                  onClick={() => onProgramChange(option.key)}
                  disabled={isFilterBusy}
                >
                  {option.shortTitle}
                </button>
              ))}
            </div>
          </div>
          <div className="mpr-filter-field">
            <label>Month</label>
            <select className="mpr-select" value={month} onChange={(e) => setMonth(e.target.value)} disabled={isFilterBusy}>
              {MONTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mpr-filter-field">
            <label>Year</label>
            <select className="mpr-select" value={year} onChange={(e) => setYear(e.target.value)} disabled={isFilterBusy}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div className="mpr-filter-actions mpc-simple-filter-actions">
            <button
              type="button"
              className={`mpr-apply-btn ${isFilterBusy ? "mpr-apply-btn--loading" : ""}`}
              onClick={handleApply}
              aria-busy={isFilterBusy}
            >
              {isFilterBusy ? <i className="mdi mdi-loading mdi-spin" /> : <i className="mdi mdi-filter" />}
              {isFilterBusy ? "Loading..." : "Filter"}
            </button>
            <button type="button" className="mpr-reset-btn" onClick={handleReset} disabled={isFilterBusy}>
              <i className="mdi mdi-refresh" />
              Reset
            </button>
          </div>
        </div>

        <div className="mpr-filter-caption">
          <span>{`Selected: ${displayMonth}`}</span>
          {validationMessage ? <span className="text-danger">{validationMessage}</span> : null}
        </div>
      </div>
    </div>
  );
};

export default MonthFilter;
